<script setup lang="ts" generic="T extends InputType">
  type Props<T extends InputType> = {
    label?: string | undefined
    placeholder?: string | undefined
    description?: string | undefined
    error?: string | undefined
    required?: boolean | undefined
    type?: T | undefined
    disabled?: boolean | undefined
    suffix?: string | undefined
  }

  // The default needs to be a function
  // See: https://github.com/vuejs/core/pull/8335
  withDefaults(defineProps<Props<T>>(), { type: () => 'text' as T, disabled: false, suffix: '' })
  const value = defineModel<InputTypeMap[T] | undefined | null>()

  const emit = defineEmits<{
    (e: 'blur', event: FocusEvent): void
  }>()
</script>

<template>
  <div class="field-container">
    <span v-show="label != null" class="field-label">
      {{ label }}
      <span v-show="required" class="required-field">*</span>
    </span>
    <div class="input-wrapper">
      <input
        v-model="value"
        :placeholder="placeholder"
        form="ignore-field"
        :disabled="disabled"
        :class="{ hasSuffix: !!suffix }"
        @blur="emit('blur', $event)"
      />
      <span v-if="suffix" class="suffix">{{ suffix }}</span>
    </div>

    <span v-show="description" class="field-description">{{ description }}</span>
    <span v-show="error != null" class="field-error">{{ error }}</span>
  </div>
</template>

<style scoped>
  .required-field {
    color: red;
    position: absolute;
    top: 0;
    left: 100%;
  }
  .field-container {
    margin: var(--spacing-xs) 0;
    position: relative;
    width: 100%;
  }

  .field-container input {
    width: 100%;
    border-radius: var(--components-form-field-corners);
    border: 1px solid var(--colours-grey-3);
    padding: 0.5rem;
  }

  .field-container input:disabled {
    background: var(--colours-grey-2);
  }

  .input-wrapper {
    display: flex;
    align-items: center;
    width: 100%;
  }

  .field-container .input-wrapper .hasSuffix {
    border-radius: var(--components-form-field-corners) 0 0 var(--components-form-field-corners);
  }

  .suffix {
    color: #444;
    padding: var(--spacing-xs);
    border: 1px solid var(--colours-grey-3);
    border-left: 0;
    border-radius: 0 var(--components-form-field-corners) var(--components-form-field-corners) 0;
  }

  .field-label {
    position: relative;
  }

  .field-error {
    position: absolute;
    top: 100%;
    color: red;
    font-size: 0.8rem;
    right: 0;
  }

  .field-description {
    position: absolute;
    color: gray;
    font-size: 0.8rem;
    top: 100%;
    left: 0;
  }
</style>
